import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Trash, CheckCircle, Plus } from '@phosphor-icons/react';

const MAX_INSTALLMENTS = 3;

// ADM-12: this is the single source of truth for the fee/enrollment UI — it lives
// inline on the student profile (LeadDetailPage) and is also reused as a thin
// wrapper by EnrollStudentDialog (the list-page quick-enroll dialog), so both
// surfaces stay in lockstep instead of drifting apart.

export const PAYMENT_INSTRUMENTS = ['Cash', 'Cheque', 'UPI', 'Bank Transfer', 'Card'];

// Lowercase codes expected by POST /api/payments' `method` field (mirrors FeesPage's
// Record Payment dialog) — distinct from PAYMENT_INSTRUMENTS above, which is the
// Title Case vocabulary POST /api/invoices' `payment_instrument` field expects.
const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'upi', label: 'UPI' },
  { value: 'bank_transfer', label: 'NEFT / Bank Transfer' },
  { value: 'card', label: 'Card' },
];

export function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

export function fmtInr(n) {
  return `Rs.${(n || 0).toLocaleString('en-IN')}`;
}

// ADM-16: client-side mirror of the backend's `_generate_installments` — proportional
// scaling of the catalog's installment_schedule (whatever share each catalog row is of
// the catalog's own installment_total) applied to the negotiated netFee, with the last
// row absorbing the rounding remainder so the rows always sum exactly to netFee. Due
// dates are spread evenly across the course's duration_months, starting at the
// enrollment date (or today) — approximate by design, the counselor can just edit
// dates directly. The backend independently validates on submit regardless.
function computeDefaultInstallments(selectedCourse, netFee, enrollmentDateStr) {
  if (!selectedCourse || netFee == null) return [];
  const schedule = Array.isArray(selectedCourse.installment_schedule) ? selectedCourse.installment_schedule : [];
  const n = Math.min(schedule.length || 0, MAX_INSTALLMENTS) || 0;
  if (n === 0) return [];

  const catalogTotal = schedule.slice(0, n).reduce((s, it) => s + (Number(it.amount) || 0), 0) || selectedCourse.installment_total || netFee;

  const rows = schedule.slice(0, n).map((it) => {
    const share = catalogTotal > 0 ? (Number(it.amount) || 0) / catalogTotal : 1 / n;
    return { amount: Math.round(netFee * share) };
  });
  // Last row absorbs the rounding remainder so the total always matches netFee exactly.
  const sumExceptLast = rows.slice(0, -1).reduce((s, r) => s + r.amount, 0);
  rows[rows.length - 1].amount = Math.round(netFee) - sumExceptLast;

  const start = enrollmentDateStr ? new Date(enrollmentDateStr) : new Date();
  const durationMonths = selectedCourse.duration_months || n;
  return rows.map((r, i) => {
    const d = new Date(start);
    if (n > 1 && durationMonths) {
      d.setMonth(d.getMonth() + Math.round((i * durationMonths) / n));
    }
    return { ...r, due_date: d.toISOString().slice(0, 10) };
  });
}

// ADM-27: plain local Indian-currency number-to-words helper (lakh/crore grouping,
// no new npm dependency) — used once for the Net Fee amount on the printable invoice.
const ONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
  'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

function twoDigitsToWords(n) {
  if (n < 20) return ONES[n];
  const t = Math.floor(n / 10), o = n % 10;
  return TENS[t] + (o ? ' ' + ONES[o] : '');
}

function threeDigitsToWords(n) {
  const h = Math.floor(n / 100), rest = n % 100;
  let s = '';
  if (h) s += ONES[h] + ' Hundred';
  if (rest) s += (s ? ' ' : '') + twoDigitsToWords(rest);
  return s;
}

export function numberToIndianWords(amount) {
  const n = Math.round(Number(amount) || 0);
  if (n === 0) return 'Rupees Zero Only';
  let rem = n;
  const crore = Math.floor(rem / 10000000); rem %= 10000000;
  const lakh = Math.floor(rem / 100000); rem %= 100000;
  const thousand = Math.floor(rem / 1000); rem %= 1000;
  const hundred = rem;

  const parts = [];
  if (crore) parts.push(threeDigitsToWords(crore) + ' Crore');
  if (lakh) parts.push(threeDigitsToWords(lakh) + ' Lakh');
  if (thousand) parts.push(threeDigitsToWords(thousand) + ' Thousand');
  if (hundred) parts.push(threeDigitsToWords(hundred));

  return `Rupees ${parts.join(' ')} Only`;
}

export function groupCoursesByCategory(courses) {
  return courses.reduce((acc, c) => {
    const cat = c.category || 'Other';
    (acc[cat] = acc[cat] || []).push(c);
    return acc;
  }, {});
}

function makeEmptyEnrollForm(lead) {
  return {
    course_id: lead?.preferred_course_id || '',
    payment_mode: 'Lumpsum',
    discount_amount: '',
    discount_reason: '',
    enrollment_date: todayStr(),
    amount_paid: 0,
    payment_instrument: 'Cash',
    cheque_number: '',
    bank_name: '',
    transaction_reference: '',
    receipt_number: '',
    refund_policy_acknowledged: false,
  };
}

export default function EnrollmentFeesSection({ lead, onEnrolled, onChange, feesUnlocked = true, courses: coursesProp, showCourseInterest = true }) {
  const leadId = lead?.id || lead?._id || '';

  const [loadingInvoice, setLoadingInvoice] = useState(true);
  const [invoice, setInvoice] = useState(null); // full invoice incl. payments[] once one exists
  const [form, setForm] = useState(() => makeEmptyEnrollForm(lead));
  const [submitting, setSubmitting] = useState(false);

  // ADM-32: EnrollStudentDialog doesn't pass `courses` (its own caller doesn't have
  // them fetched), so this component fetches its own copy for it exactly as before.
  // LeadDetailPage does pass `courses` (already fetched there for the Course Interest
  // selector below), so we use that instead of firing a duplicate GET /api/courses.
  const [ownCourses, setOwnCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const courses = coursesProp || ownCourses;

  const [savingCourseInterest, setSavingCourseInterest] = useState(false);

  // ADM-31: batch allocation — independent of invoice creation, assigned via its
  // own PUT /api/leads/{id}/batch call the moment a batch is picked.
  const [batches, setBatches] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [assignedBatchId, setAssignedBatchId] = useState(lead?.batch_id || '');
  const [assigningBatch, setAssigningBatch] = useState(false);

  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [payForm, setPayForm] = useState({ amount: '', method: 'cash', reference: '' });
  const [recordingPayment, setRecordingPayment] = useState(false);

  // ADM-16: counselor-controlled installment split. `installmentRows` is the live
  // editable preview; `installmentsCustomized` gates whether the effect below is
  // still allowed to auto-recompute it from the catalog (true once the counselor
  // hits "Customize split", so their manual edits never get clobbered).
  const [installmentRows, setInstallmentRows] = useState([]);
  const [installmentsCustomized, setInstallmentsCustomized] = useState(false);

  // ADM-32: mirrors the installmentsCustomized clobber-avoidance pattern above —
  // once the counselor has manually picked a course in the enrollment form's own
  // selector, a later change to lead.preferred_course_id (via the new top-of-card
  // Course Interest selector) must not silently overwrite their choice.
  const [courseIdTouched, setCourseIdTouched] = useState(false);

  const fetchInvoice = useCallback(async () => {
    if (!leadId) { setLoadingInvoice(false); return; }
    setLoadingInvoice(true);
    try {
      const { data } = await api.get(`/api/invoices?lead_id=${leadId}`);
      const list = data.invoices || [];
      // The enrollment invoice is the one carrying payment_mode or a resolved
      // course_fee_total — legacy/manual fee invoices (FeesPage) have neither.
      const enrollmentInv = list.find(i => i.payment_mode || i.course_fee_total);
      if (enrollmentInv) {
        const { data: full } = await api.get(`/api/invoices/${enrollmentInv.id}`);
        setInvoice(full);
      } else {
        setInvoice(null);
      }
    } catch (e) {
      console.error(e);
      setInvoice(null);
    } finally {
      setLoadingInvoice(false);
    }
  }, [leadId]);

  useEffect(() => { fetchInvoice(); }, [fetchInvoice]);

  // Reset the create-form whenever we land on a different lead.
  useEffect(() => { setForm(makeEmptyEnrollForm(lead)); setCourseIdTouched(false); }, [leadId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ADM-32: if the counselor changes course interest via the new top-of-card
  // selector while the enrollment form below is already mounted (and its own
  // course field hasn't been manually touched yet), pick up the new value —
  // but never clobber a course the counselor already picked in the form itself.
  useEffect(() => {
    if (courseIdTouched) return;
    if (!lead?.preferred_course_id) return;
    setForm(f => (f.course_id === lead.preferred_course_id ? f : { ...f, course_id: lead.preferred_course_id }));
  }, [lead?.preferred_course_id, courseIdTouched]);

  // True once we know there's no invoice yet (form mode) — gates both the batch
  // picker below (no point offering batches while showing the read-mostly ledger)
  // and, separately, this component's own course-catalog fetch.
  const inFormMode = !loadingInvoice && !invoice;
  // Only fetch our own course catalog in form mode AND when the parent hasn't
  // already handed us `courses` (LeadDetailPage's case) — avoids a duplicate
  // GET /api/courses when the parent fetched the same list for the Course
  // Interest selector above.
  const needsCourses = inFormMode && !coursesProp;
  useEffect(() => {
    if (!needsCourses) return;
    let cancelled = false;
    setCoursesLoading(true);
    api.get('/api/courses')
      .then(({ data }) => { if (!cancelled) setOwnCourses(data.courses || []); })
      .catch(e => console.error(e))
      .finally(() => { if (!cancelled) setCoursesLoading(false); });
    return () => { cancelled = true; };
  }, [needsCourses]);

  // Pre-select the lead's already-assigned batch (if any) when the component
  // mounts or the lead changes, in case the counselor is revisiting this form.
  useEffect(() => { setAssignedBatchId(lead?.batch_id || ''); }, [leadId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch the batch list for the selected course whenever it changes — optional,
  // doesn't block the rest of the enrollment form.
  useEffect(() => {
    if (!inFormMode || !form.course_id) { setBatches([]); return; }
    let cancelled = false;
    setBatchesLoading(true);
    api.get(`/api/courses/${form.course_id}/batches`)
      .then(({ data }) => { if (!cancelled) setBatches(data.batches || []); })
      .catch(e => { console.error(e); if (!cancelled) setBatches([]); })
      .finally(() => { if (!cancelled) setBatchesLoading(false); });
    return () => { cancelled = true; };
  }, [inFormMode, form.course_id]);

  const handleSelectBatch = async (batchId) => {
    if (!batchId || !leadId) return;
    setAssigningBatch(true);
    try {
      await api.put(`/api/leads/${leadId}/batch`, { batch_id: batchId });
      setAssignedBatchId(batchId);
      toast.success('Batch assigned');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign batch');
    } finally { setAssigningBatch(false); }
  };

  const notifyChange = () => { onChange?.(); onEnrolled?.(); };

  // ADM-32: the new always-visible Course Interest selector at the top of this
  // component — separate from `form.course_id` (the enrollment-invoice form's own
  // course field below), since a counselor should be able to record/change course
  // interest long before there's an invoice to create. Mirrors the save/toast
  // pattern already used for batch assignment above.
  const handleCourseInterestChange = async (courseId) => {
    if (!leadId) return;
    setSavingCourseInterest(true);
    try {
      await api.put(`/api/leads/${leadId}`, { preferred_course_id: courseId });
      toast.success('Course interest updated');
      notifyChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSavingCourseInterest(false); }
  };

  const selectedCourse = courses.find(c => c.id === form.course_id);
  const hasInstallmentPlan = !!selectedCourse && selectedCourse.installment_total != null;
  const officialFee = selectedCourse
    ? (form.payment_mode === 'Installment' ? selectedCourse.installment_total : selectedCourse.one_time_fee)
    : null;
  const discount = Number(form.discount_amount) || 0;
  const netFee = officialFee != null ? Math.max(officialFee - discount, 0) : null;

  const showCheque = form.payment_instrument === 'Cheque';
  const showTxnRef = form.payment_instrument === 'UPI' || form.payment_instrument === 'Bank Transfer';

  const showInstallmentEditor = form.payment_mode === 'Installment' && hasInstallmentPlan && netFee != null;

  // Auto-recompute the default installment rows from the catalog whenever something
  // that changes netFee changes — but only while the counselor hasn't customized the
  // split, so a manual edit is never silently overwritten.
  useEffect(() => {
    if (installmentsCustomized) return;
    if (!showInstallmentEditor) { setInstallmentRows([]); return; }
    setInstallmentRows(computeDefaultInstallments(selectedCourse, netFee, form.enrollment_date));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.course_id, form.payment_mode, discount, officialFee, netFee, installmentsCustomized]);

  const installmentTotal = installmentRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const installmentsMatchNet = netFee != null && Math.round(installmentTotal) === Math.round(netFee);

  const handleCourseChange = (courseId) => {
    setCourseIdTouched(true);
    const c = courses.find(x => x.id === courseId);
    setForm(f => ({
      ...f,
      course_id: courseId,
      // Fall back to Lumpsum if the newly selected course has no installment plan.
      payment_mode: (c && c.installment_total == null) ? 'Lumpsum' : f.payment_mode,
    }));
  };

  const handleToggleCustomizeSplit = () => {
    // Toggling off ("Reset to standard split") just flips the flag — the effect
    // above regenerates default rows from scratch on the next render.
    setInstallmentsCustomized(v => !v);
  };

  const handleAddInstallmentRow = () => {
    if (installmentRows.length >= MAX_INSTALLMENTS) return;
    setInstallmentRows(rows => [...rows, { amount: 0, due_date: todayStr() }]);
  };

  const handleRemoveInstallmentRow = (idx) => {
    setInstallmentRows(rows => rows.filter((_, i) => i !== idx));
  };

  const updateInstallmentRow = (idx, key, val) => {
    setInstallmentRows(rows => rows.map((r, i) => (i === idx ? { ...r, [key]: val } : r)));
  };

  const validate = () => {
    if (!form.course_id) return 'Please select a course';
    if (!assignedBatchId) return batches.length === 0
      ? 'This course has no batches yet — add one under Courses & Batches before enrolling'
      : 'Please select a batch';
    if (!form.payment_mode) return 'Please select a payment plan';
    if (form.payment_mode === 'Installment' && !hasInstallmentPlan) return 'This course has no installment plan; use Lumpsum';
    if (discount > 0 && !form.discount_reason.trim()) return 'A reason is required when a discount is applied';
    if (showInstallmentEditor && !installmentsMatchNet) return `Installments must add up to exactly ${fmtInr(netFee)}`;
    if (!form.payment_instrument) return 'Please select a payment instrument';
    if (!form.enrollment_date) return 'Enrollment date is required';
    if (!form.refund_policy_acknowledged) return 'Please acknowledge the refund policy before enrolling';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const error = validate();
    if (error) { toast.error(error); return; }

    const payload = {
      student_name: lead?.student_name || '',
      course_id: form.course_id,
      payment_mode: form.payment_mode,
      discount_amount: discount,
      discount_reason: form.discount_reason.trim(),
      amount_paid: Number(form.amount_paid) || 0,
      payment_instrument: form.payment_instrument,
      refund_policy_acknowledged: !!form.refund_policy_acknowledged,
      enrollment_date: form.enrollment_date,
    };
    if (leadId) payload.lead_id = leadId;
    if (lead?.branch_id) payload.branch_id = lead.branch_id;
    if (form.payment_mode === 'Installment') {
      payload.installments = installmentRows.map((r, i) => ({
        sequence: i + 1,
        amount: Number(r.amount),
        due_date: r.due_date,
        status: 'pending',
        paid_on: null,
        receipt_number: '',
      }));
    }
    if (showCheque) {
      if (form.cheque_number.trim()) payload.cheque_number = form.cheque_number.trim();
      if (form.bank_name.trim()) payload.bank_name = form.bank_name.trim();
    }
    if (showTxnRef && form.transaction_reference.trim()) payload.transaction_reference = form.transaction_reference.trim();
    if (form.receipt_number.trim()) payload.receipt_number = form.receipt_number.trim();

    setSubmitting(true);
    try {
      const { data } = await api.post('/api/invoices', payload);

      // NOTE: backend/routes/fee_routes.py's InvoiceCreate model has no
      // discount_reason field, so it's silently dropped from the POST above (only
      // discount_amount is persisted on create). Persist the reason with a
      // follow-up PUT, which *does* accept discount_reason as a free-form
      // financial field on a non-paid invoice — this is a workaround, not a fix;
      // the real fix is adding discount_reason to InvoiceCreate server-side.
      if (discount > 0 && form.discount_reason.trim() && data?.id) {
        try {
          await api.put(`/api/invoices/${data.id}`, { discount_reason: form.discount_reason.trim() });
        } catch (e) {
          console.error('Failed to persist discount reason', e);
        }
      }

      if (Number(form.amount_paid) > 0) {
        toast.success(`Enrolled — ${data.enrollment_number}, ${fmtInr(data.course_fee_total)}`);
      } else {
        toast.success(`Invoice created — ${data.enrollment_number}, ${fmtInr(data.course_fee_total)} due · stays in Fee Pending until a payment is recorded`);
      }

      setForm(makeEmptyEnrollForm(lead));
      setInstallmentsCustomized(false);
      setInstallmentRows([]);
      await fetchInvoice();
      notifyChange();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 422 && detail) {
        toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
      } else {
        toast.error(detail || 'Failed to enroll student');
      }
    } finally { setSubmitting(false); }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!payForm.amount || Number(payForm.amount) <= 0) { toast.error('Enter an amount greater than Rs.0'); return; }
    if (!invoice?.id) return;
    setRecordingPayment(true);
    try {
      await api.post('/api/payments', {
        invoice_id: invoice.id,
        amount: Number(payForm.amount),
        method: payForm.method,
        transaction_id: payForm.reference.trim() || undefined,
      });
      toast.success('Payment recorded');
      setPayForm({ amount: '', method: 'cash', reference: '' });
      setShowRecordPayment(false);
      await fetchInvoice();
      notifyChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    } finally { setRecordingPayment(false); }
  };

  // ADM-16: rebuilt into an actual printable invoice layout (was a bare list of
  // <p> tags before) — still window.open + window.print(), no new API calls beyond
  // the existing GET /api/invoices/{id}/receipt, which already returns { invoice, payments }.
  const openReceipt = async () => {
    if (!invoice?.id) return;
    try {
      // ADM-27: fetch the org profile in parallel with the receipt — independent
      // requests, no need to serialize them.
      const [res, orgRes] = await Promise.all([
        api.get(`/api/invoices/${invoice.id}/receipt`),
        api.get('/api/settings/organization'),
      ]);
      const d = res.data;
      const inv = d.invoice || {};
      const org = orgRes.data || {};
      const esc = (s) => (s == null ? '' : String(s));
      const money = (n) => `Rs.${(n || 0).toLocaleString('en-IN')}`;

      const officialFeeV = inv.course_fee_total ?? inv.amount ?? 0;
      const discountV = inv.discount_amount || 0;
      const netV = inv.net_amount ?? (officialFeeV - discountV);
      const paidV = inv.paid_amount || 0;
      const balanceV = inv.balance_due != null ? inv.balance_due : Math.max(netV - paidV, 0);
      const installmentsV = inv.installments || [];
      const paymentsV = d.payments || [];
      const datePrinted = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      const amountInWords = numberToIndianWords(netV);

      const orgName = org.name && org.name.trim() ? org.name : 'Admitrix';
      const docTitle = org.gst_registered ? 'TAX INVOICE' : 'FEE INVOICE';

      const addressLines = [org.address_line1, org.address_line2].filter(s => s && s.trim());
      const cityStatePin = [org.city, org.state, org.pincode].filter(s => s && s.trim()).join(', ');
      if (cityStatePin) addressLines.push(cityStatePin);
      const addressHtml = addressLines.map(l => `<div>${esc(l)}</div>`).join('');
      const gstinPanHtml = [
        org.gstin && org.gstin.trim() ? `<div>GSTIN: ${esc(org.gstin)}</div>` : '',
        org.pan && org.pan.trim() ? `<div>PAN: ${esc(org.pan)}</div>` : '',
      ].join('');

      const bankFields = [
        ['Account Name', org.bank_account_name],
        ['Account Number', org.bank_account_number],
        ['IFSC', org.bank_ifsc],
        ['Bank', org.bank_name],
        ['Branch', org.bank_branch],
      ].filter(([, v]) => v && String(v).trim());
      const hasBankDetails = bankFields.length > 0;
      const bankDetailsHtml = bankFields.map(([label, v]) => `<div><strong>${label}:</strong> ${esc(v)}</div>`).join('');

      const signatoryLabel = org.authorized_signatory_name && org.authorized_signatory_name.trim()
        ? esc(org.authorized_signatory_name)
        : 'Authorized Signatory';

      const paymentsHtml = paymentsV.map(p => `
        <tr>
          <td>${esc(p.receipt_number) || '-'}</td>
          <td>${esc(p.method) || '-'}</td>
          <td>${money(p.amount)}</td>
          <td>${p.created_at ? new Date(p.created_at).toLocaleDateString('en-IN') : '-'}</td>
        </tr>`).join('');

      const installmentsHtml = installmentsV.map(it => `
        <tr>
          <td>${it.sequence ?? '-'}</td>
          <td>${esc(it.due_date) || '-'}</td>
          <td>${it.status === 'partial' ? `${money(it.paid_amount)} / ${money(it.amount)}` : money(it.amount)}</td>
          <td>${it.status === 'paid' ? 'Paid' : it.status === 'partial' ? 'Partial' : 'Pending'}</td>
        </tr>`).join('');

      const html = `<!DOCTYPE html><html><head><title>Invoice - ${esc(inv.invoice_number)}</title>
<style>
  @page { margin: 24mm 18mm; }
  * { box-sizing: border-box; }
  body { font-family: Georgia, 'Times New Roman', serif; max-width: 760px; margin: 0 auto; padding: 32px 24px; color: #1a1a1a; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #1a1a1a; padding-bottom: 16px; margin-bottom: 20px; }
  .wordmark { font-family: Arial, sans-serif; font-weight: 800; font-size: 20px; color: #E04B2A; letter-spacing: 0.02em; }
  .wordmark img { max-height: 48px; display: block; }
  .doc-title { font-family: Arial, sans-serif; font-weight: 800; font-size: 22px; color: #E04B2A; letter-spacing: 0.08em; margin-top: 6px; }
  .org-address { font-family: Arial, sans-serif; font-size: 10px; color: #888; line-height: 1.5; margin-top: 6px; }
  .meta { text-align: right; font-size: 12px; color: #444; line-height: 1.6; }
  .meta strong { color: #1a1a1a; }
  h3 { font-family: Arial, sans-serif; font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em; color: #555; margin: 22px 0 8px; }
  .bill-to p { margin: 2px 0; font-size: 13px; }
  table { width: 100%; border-collapse: collapse; margin-top: 4px; font-size: 12.5px; }
  th, td { border: 1px solid #ccc; padding: 7px 10px; text-align: left; }
  th { background: #f6f6f4; font-family: Arial, sans-serif; font-weight: 700; font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.04em; color: #444; }
  .fee-table td:last-child, .fee-table th:last-child { text-align: right; }
  .net-row td { font-weight: 800; font-size: 14px; color: #E04B2A; border-top: 2px solid #1a1a1a; }
  .amount-words { font-size: 11px; color: #555; font-style: italic; margin-top: 6px; }
  .bank-box { margin-top: 20px; padding: 12px 14px; border: 1px solid #ccc; border-radius: 4px; background: #fafaf9; }
  .bank-box h3 { margin: 0 0 8px; }
  .bank-box div { font-size: 11.5px; color: #444; line-height: 1.7; }
  .footer-note { margin-top: 28px; font-size: 11px; color: #777; font-style: italic; }
  .signatures { display: flex; gap: 60px; margin-top: 56px; }
  .sig { flex: 1; text-align: center; }
  .sig .line { border-top: 1px solid #1a1a1a; margin-bottom: 6px; padding-top: 40px; }
  .sig .label { font-family: Arial, sans-serif; font-size: 10.5px; color: #555; text-transform: uppercase; letter-spacing: 0.05em; }
  @media print { body { padding: 0; } }
</style></head><body>

  <div class="header">
    <div>
      <div class="wordmark">${org.logo_url && org.logo_url.trim() ? `<img src="${esc(org.logo_url)}" alt="${esc(orgName)}" />` : esc(orgName)}</div>
      <div class="doc-title">${docTitle}</div>
      ${addressHtml || gstinPanHtml ? `<div class="org-address">${addressHtml}${gstinPanHtml}</div>` : ''}
    </div>
    <div class="meta">
      <div><strong>Invoice #</strong> ${esc(inv.invoice_number) || '-'}</div>
      ${inv.enrollment_number ? `<div><strong>Enrollment #</strong> ${esc(inv.enrollment_number)}</div>` : ''}
      <div><strong>Date Printed</strong> ${datePrinted}</div>
    </div>
  </div>

  <h3>Bill To</h3>
  <div class="bill-to">
    <p><strong>${esc(inv.student_name) || '-'}</strong></p>
    ${inv.parent_name ? `<p>Parent/Guardian: ${esc(inv.parent_name)}${inv.parent_phone ? ` &middot; ${esc(inv.parent_phone)}` : ''}</p>` : ''}
    ${inv.course_name ? `<p>Course: ${esc(inv.course_name)}</p>` : ''}
  </div>

  <h3>Fee Breakdown</h3>
  <table class="fee-table">
    <tbody>
      <tr><td>Official Fee</td><td>${money(officialFeeV)}</td></tr>
      <tr><td>Discount${inv.discount_reason ? ` (${esc(inv.discount_reason)})` : ''}</td><td>${discountV ? `-${money(discountV)}` : money(0)}</td></tr>
      <tr class="net-row"><td>Net Fee</td><td>${money(netV)}</td></tr>
      <tr><td>Paid</td><td>${money(paidV)}</td></tr>
      <tr><td>Balance Due</td><td>${money(balanceV)}</td></tr>
    </tbody>
  </table>
  <p class="amount-words">Amount in Words: ${esc(amountInWords)}</p>

  ${installmentsV.length > 0 ? `
  <h3>Installment Schedule</h3>
  <table>
    <thead><tr><th>Sequence</th><th>Due Date</th><th>Amount</th><th>Status</th></tr></thead>
    <tbody>${installmentsHtml}</tbody>
  </table>` : ''}

  ${paymentsV.length > 0 ? `
  <h3>Payments</h3>
  <table>
    <thead><tr><th>Receipt #</th><th>Method</th><th>Amount</th><th>Date</th></tr></thead>
    <tbody>${paymentsHtml}</tbody>
  </table>` : ''}

  ${hasBankDetails ? `
  <div class="bank-box">
    <h3>Bank Details for Payment</h3>
    ${bankDetailsHtml}
  </div>` : ''}

  <p class="footer-note">Fees once paid are non-refundable.</p>

  <div class="signatures">
    <div class="sig"><div class="line"></div><div class="label">${signatoryLabel}</div></div>
    <div class="sig"><div class="line"></div><div class="label">Parent/Guardian Signature</div></div>
  </div>

</body></html>`;
      const w = window.open('', '_blank');
      w.document.write(html);
      w.document.close();
      setTimeout(() => w.print(), 500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load receipt');
    }
  };

  // ADM-32: prominent, always-visible Course Interest selector — rendered above
  // everything else in this component regardless of `feesUnlocked`, unlike the
  // fee/invoice functionality below which stays gated until the stage unlocks it.
  // Locked read-only the moment the lead reaches "enrolled" -- enrolled is a
  // system-driven stage (only ever set via payment, never manually chosen), so
  // the course that drove that enrollment's fees must stop being editable by
  // anyone at that point, not just soft-blocked for some roles.
  const isEnrolled = lead?.stage === 'enrolled';
  const courseInterestGroups = groupCoursesByCategory(courses);
  const enrolledCourseName = isEnrolled
    ? (Object.values(courseInterestGroups).flat().find(c => c.id === lead?.preferred_course_id)?.name || '-')
    : null;
  const courseInterestSelector = (
    <div data-testid="course-interest-block">
      <Label className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground">Course Interest</Label>
      {isEnrolled ? (
        <div data-testid="course-interest-locked" className="h-9 w-full rounded-lg text-xs border border-border px-3 bg-secondary/50 text-foreground mt-1 flex items-center">
          {enrolledCourseName}
        </div>
      ) : (
        <select
          value={lead?.preferred_course_id || ''}
          onChange={e => handleCourseInterestChange(e.target.value)}
          disabled={savingCourseInterest}
          data-testid="course-interest-select"
          className="h-9 w-full rounded-lg text-xs border border-border px-3 bg-card text-foreground mt-1 disabled:opacity-60"
        >
          <option value="">Select course...</option>
          {Object.entries(courseInterestGroups).map(([cat, list]) => (
            <optgroup key={cat} label={cat}>
              {list.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </optgroup>
          ))}
        </select>
      )}
    </div>
  );

  let body;
  if (!feesUnlocked) {
    body = (
      <p className="text-[12px] text-muted-foreground/70" data-testid="enrollment-fees-locked-inline">
        🔒 Enrollment opens once this lead reaches Application stage.
      </p>
    );
  } else if (loadingInvoice) {
    body = <p className="text-[11px] text-muted-foreground font-mono">loading fees...</p>;
  } else if (invoice) {
    const officialFeeV = invoice.course_fee_total ?? invoice.amount ?? 0;
    const discountV = invoice.discount_amount || 0;
    const netV = invoice.net_amount ?? (officialFeeV - discountV);
    const paidV = invoice.paid_amount || 0;
    const pendingV = invoice.balance_due != null ? invoice.balance_due : Math.max(netV - paidV, 0);
    const installments = invoice.installments || [];
    const payments = invoice.payments || [];

    body = (
      <div data-testid="enrollment-fees-ledger" className="space-y-3">
        <div className="divide-y divide-border text-[12px]">
          <LedgerRow label="Official Fee" value={fmtInr(officialFeeV)} />
          <LedgerRow label="Discount" value={discountV ? `-${fmtInr(discountV)}` : fmtInr(0)} />
          <LedgerRow label="Net Fee" value={fmtInr(netV)} bold />
          <LedgerRow label="Paid" value={fmtInr(paidV)} color="#10A882" />
          <LedgerRow label="Pending" value={fmtInr(pendingV)} color={pendingV > 0 ? '#EF4444' : '#10A882'} />
        </div>
        {invoice.discount_reason && (
          <p className="text-[10px] text-muted-foreground">Discount reason: {invoice.discount_reason}</p>
        )}

        {installments.length > 0 && (
          <div className="pt-2 border-t border-border" data-testid="installment-schedule">
            <p className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground mb-1.5">Installment Schedule</p>
            <div className="space-y-1">
              {installments.map((it, i) => {
                const statusLabel = it.status === 'paid' ? 'Paid' : it.status === 'partial' ? 'Partial' : 'Planned';
                const statusColor = it.status === 'paid' ? '#10A882' : it.status === 'partial' ? '#E5A100' : '#808080';
                return (
                  <div key={i} className="flex items-center justify-between text-[11px]">
                    <span className="text-muted-foreground">#{it.sequence || i + 1} &middot; due {it.due_date || '-'}</span>
                    <span className="flex items-center gap-2">
                      <span className="font-mono text-foreground">
                        {it.status === 'partial' ? `${fmtInr(it.paid_amount)} / ${fmtInr(it.amount)}` : fmtInr(it.amount)}
                      </span>
                      <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: statusColor }}>
                        {statusLabel}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {payments.length > 0 && (
          <div className="pt-2 border-t border-border" data-testid="payments-list">
            <p className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground mb-1.5">Payments</p>
            <div className="space-y-1">
              {payments.map((p, i) => (
                <div key={i} className="flex items-center justify-between text-[11px]">
                  <span className="text-muted-foreground font-mono">{p.receipt_number || '-'} &middot; {p.method}</span>
                  <span className="font-mono text-foreground">{fmtInr(p.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 pt-2 border-t border-border">
          <Button type="button" size="sm" variant="outline" onClick={() => setShowRecordPayment(v => !v)} data-testid="toggle-record-payment" className="h-8 rounded-lg text-xs">
            {showRecordPayment ? 'Cancel' : 'Record Payment'}
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={openReceipt} data-testid="view-receipt-btn" className="h-8 rounded-lg text-xs">
            Download Invoice
          </Button>
        </div>

        {showRecordPayment && (
          <form onSubmit={handleRecordPayment} className="grid grid-cols-3 gap-2 pt-2" data-testid="record-payment-form">
            <Input type="number" min="0" placeholder="Amount" value={payForm.amount} onChange={e => setPayForm({ ...payForm, amount: e.target.value })} data-testid="record-payment-amount" className="h-9 rounded-lg text-xs" />
            <select value={payForm.method} onChange={e => setPayForm({ ...payForm, method: e.target.value })} data-testid="record-payment-method" className="h-9 rounded-lg text-xs border border-border px-2 bg-card text-foreground">
              {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            <Input placeholder="Reference (optional)" value={payForm.reference} onChange={e => setPayForm({ ...payForm, reference: e.target.value })} data-testid="record-payment-reference" className="h-9 rounded-lg text-xs" />
            <Button type="submit" disabled={recordingPayment} data-testid="record-payment-submit" className="col-span-3 h-9 rounded-lg text-xs font-bold text-white" style={{ backgroundColor: '#E04B2A' }}>
              {recordingPayment ? 'Recording...' : 'Record Payment'}
            </Button>
          </form>
        )}
      </div>
    );
  } else {
    // -------- Enrollment form: no enrollment invoice yet --------
    const courseGroups = groupCoursesByCategory(courses);

    body = (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="enrollment-fees-form">
      <div>
        <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Course *</Label>
        <select value={form.course_id} onChange={e => handleCourseChange(e.target.value)} data-testid="fees-course" disabled={coursesLoading}
          className="h-9 w-full rounded-lg text-xs border border-border px-3 bg-card text-foreground disabled:opacity-60" required>
          <option value="">{coursesLoading ? 'Loading courses...' : 'Select course...'}</option>
          {Object.entries(courseGroups).map(([cat, list]) => (
            <optgroup key={cat} label={cat}>
              {list.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </optgroup>
          ))}
        </select>
      </div>

      {/* ADM-54: batch allocation — REQUIRED, independent of invoice creation.
          Assignment fires immediately on selection via PUT /api/leads/{id}/batch,
          not bundled into the POST /api/invoices payload below; validate() and the
          submit button both block enrollment until assignedBatchId is set, and
          create_invoice enforces the same rule server-side. */}
      {form.course_id && (
        <div>
          <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Batch *</Label>
          {batchesLoading ? (
            <p className="text-[11px] text-muted-foreground mt-1">Loading batches...</p>
          ) : batches.length === 0 ? (
            <p className="text-[11px] text-destructive mt-1" data-testid="batch-picker-empty">No batches for this course yet — add one under Courses &amp; Batches before enrolling.</p>
          ) : (
            <select
              value={assignedBatchId}
              onChange={e => handleSelectBatch(e.target.value)}
              disabled={assigningBatch}
              data-testid="batch-picker"
              className="h-9 w-full rounded-lg text-xs border border-border px-3 bg-card text-foreground disabled:opacity-60"
              required
            >
              <option value="">Select batch...</option>
              {batches.map(b => {
                const full = (b.enrolled_count || 0) >= (b.capacity || 0);
                return (
                  <option key={b.id} value={b.id} data-testid={`batch-option-${b.id}`}>
                    {b.name} — {b.enrolled_count || 0}/{b.capacity || 0} enrolled{full ? ' (Full)' : ''}
                  </option>
                );
              })}
            </select>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Plan *</Label>
          <select value={form.payment_mode} onChange={e => setForm({ ...form, payment_mode: e.target.value })} data-testid="fees-payment-mode"
            className="h-9 w-full rounded-lg text-xs border border-border px-3 bg-card text-foreground" required>
            <option value="Lumpsum">Lumpsum</option>
            <option value="Installment" disabled={!!selectedCourse && !hasInstallmentPlan}>
              Installments{hasInstallmentPlan ? ` (${selectedCourse.installment_schedule?.length || 0})` : ''}
            </option>
          </select>
          {selectedCourse && !hasInstallmentPlan && (
            <p className="text-[10px] text-muted-foreground mt-1">No installment plan for this course &mdash; Lumpsum only.</p>
          )}
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Official Fee</Label>
          <div data-testid="fees-official-fee" className="h-9 rounded-lg text-xs border border-border px-3 bg-secondary/50 text-foreground flex items-center font-semibold">
            {officialFee != null ? fmtInr(officialFee) : '—'}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Discount</Label>
          <Input type="number" min="0" value={form.discount_amount} onChange={e => setForm({ ...form, discount_amount: e.target.value })} data-testid="fees-discount-amount" className="h-9 rounded-lg text-xs" />
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Net Fee</Label>
          <div data-testid="fees-net-fee" className="h-9 rounded-lg text-xs border border-border px-3 bg-secondary/50 text-foreground flex items-center font-bold">
            {netFee != null ? fmtInr(netFee) : '—'}
          </div>
        </div>
      </div>
      {discount > 0 && (
        <div>
          <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Discount Reason *</Label>
          <Input value={form.discount_reason} onChange={e => setForm({ ...form, discount_reason: e.target.value })} data-testid="fees-discount-reason" className="h-9 rounded-lg text-xs" required />
        </div>
      )}

      {showInstallmentEditor && (
        <InstallmentEditor
          rows={installmentRows}
          netFee={netFee}
          customized={installmentsCustomized}
          total={installmentTotal}
          matches={installmentsMatchNet}
          onToggleCustomize={handleToggleCustomizeSplit}
          onAddRow={handleAddInstallmentRow}
          onRemoveRow={handleRemoveInstallmentRow}
          onUpdateRow={updateInstallmentRow}
        />
      )}

      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border">
        <div>
          <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">First Payment</Label>
          <Input type="number" min="0" value={form.amount_paid} onChange={e => setForm({ ...form, amount_paid: e.target.value })} data-testid="fees-amount-paid" className="h-9 rounded-lg text-xs" />
          <p className="text-[10px] text-muted-foreground mt-1">Above Rs.0 moves this lead to Enrolled automatically.</p>
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Payment Instrument *</Label>
          <select value={form.payment_instrument} onChange={e => setForm({ ...form, payment_instrument: e.target.value })} data-testid="fees-payment-instrument"
            className="h-9 w-full rounded-lg text-xs border border-border px-3 bg-card text-foreground" required>
            {PAYMENT_INSTRUMENTS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>
      {showCheque && (
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Cheque Number</Label>
            <Input value={form.cheque_number} onChange={e => setForm({ ...form, cheque_number: e.target.value })} data-testid="fees-cheque-number" className="h-9 rounded-lg text-xs" /></div>
          <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Bank Name</Label>
            <Input value={form.bank_name} onChange={e => setForm({ ...form, bank_name: e.target.value })} data-testid="fees-bank-name" className="h-9 rounded-lg text-xs" /></div>
        </div>
      )}
      {showTxnRef && (
        <div>
          <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">{form.payment_instrument === 'UPI' ? 'UPI Reference' : 'NEFT UTR / Reference'}</Label>
          <Input value={form.transaction_reference} onChange={e => setForm({ ...form, transaction_reference: e.target.value })} data-testid="fees-transaction-reference" className="h-9 rounded-lg text-xs" />
        </div>
      )}
      <div>
        <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Receipt Number</Label>
        <Input value={form.receipt_number} onChange={e => setForm({ ...form, receipt_number: e.target.value })} data-testid="fees-receipt-number" className="h-9 rounded-lg text-xs" />
      </div>

      <div>
        <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Enrollment Date *</Label>
        <Input type="date" value={form.enrollment_date} onChange={e => setForm({ ...form, enrollment_date: e.target.value })} data-testid="fees-enrollment-date" className="h-9 rounded-lg text-xs" required />
      </div>

      <div className="flex items-center gap-2">
        <Checkbox id="fees-refund-ack" checked={form.refund_policy_acknowledged} onCheckedChange={(v) => setForm({ ...form, refund_policy_acknowledged: !!v })} data-testid="fees-refund-ack" />
        <Label htmlFor="fees-refund-ack" className="text-xs font-normal text-foreground">Fees are non-refundable &mdash; acknowledged by student *</Label>
      </div>

      <Button
        type="submit"
        disabled={submitting || !assignedBatchId || (showInstallmentEditor && !installmentsMatchNet)}
        data-testid="fees-submit-btn"
        className="w-full h-9 rounded-lg text-xs font-bold text-white disabled:opacity-50"
        style={{ backgroundColor: '#E04B2A' }}
      >
        {submitting ? 'Saving...' : 'Enroll & Create Invoice'}
      </Button>
    </form>
    );
  }

  return (
    <div className="space-y-3" data-testid="enrollment-fees-section">
      {showCourseInterest && courseInterestSelector}
      {body}
    </div>
  );
}

function LedgerRow({ label, value, bold, color }) {
  return (
    <div className="flex items-center justify-between py-2 first:pt-0 last:pb-0">
      <span className="text-muted-foreground">{label}</span>
      <span className={bold ? 'font-bold text-foreground text-[13px]' : 'font-mono text-foreground text-[12px]'} style={color ? { color } : undefined}>{value}</span>
    </div>
  );
}

// ADM-16: counselor-controlled installment split, shown inline while creating the
// enrollment invoice. Default rows are seeded from the catalog (computeDefaultInstallments)
// and locked to that row count; "Customize split" unlocks add/remove up to MAX_INSTALLMENTS.
function InstallmentEditor({ rows, netFee, customized, total, matches, onToggleCustomize, onAddRow, onRemoveRow, onUpdateRow }) {
  return (
    <div className="pt-2 border-t border-border space-y-2" data-testid="installment-editor">
      <div className="flex items-center justify-between">
        <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Installment Split</Label>
        <button
          type="button"
          onClick={onToggleCustomize}
          data-testid="installment-customize-toggle"
          className="text-[10px] font-medium text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
        >
          {customized ? 'Reset to standard split' : 'Customize split'}
        </button>
      </div>

      <div className="space-y-1.5">
        {/* Column header */}
        <div className="grid grid-cols-[28px_1fr_1fr_28px] gap-2 items-center px-0.5">
          <span className="text-[9px] uppercase tracking-wider text-muted-foreground/70 font-semibold">#</span>
          <span className="text-[9px] uppercase tracking-wider text-muted-foreground/70 font-semibold">Amount</span>
          <span className="text-[9px] uppercase tracking-wider text-muted-foreground/70 font-semibold">Due Date</span>
          <span />
        </div>
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[28px_1fr_1fr_28px] gap-2 items-center" data-testid={`installment-row-${i}`}>
            <span className="text-[11px] text-muted-foreground font-mono">{i + 1}</span>
            <Input
              type="number"
              min="0"
              value={r.amount}
              onChange={e => onUpdateRow(i, 'amount', e.target.value)}
              data-testid={`installment-row-amount-${i}`}
              className="h-8 rounded-lg text-xs"
            />
            <Input
              type="date"
              value={r.due_date || ''}
              onChange={e => onUpdateRow(i, 'due_date', e.target.value)}
              data-testid={`installment-row-due-date-${i}`}
              className="h-8 rounded-lg text-xs"
            />
            {customized ? (
              <button
                type="button"
                onClick={() => onRemoveRow(i)}
                data-testid={`installment-remove-row-${i}`}
                className="h-8 w-8 flex items-center justify-center text-muted-foreground hover:text-red-500 transition-colors"
              >
                <Trash size={13} />
              </button>
            ) : <span />}
          </div>
        ))}
      </div>

      {customized && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onAddRow}
          disabled={rows.length >= MAX_INSTALLMENTS}
          data-testid="installment-add-row"
          className="h-7 rounded-lg text-[11px] gap-1"
        >
          <Plus size={11} /> Add installment
        </Button>
      )}

      <div
        className="flex items-center justify-between pt-1.5 border-t border-border text-[11px] font-medium"
        data-testid="installment-total-footer"
      >
        <span className="text-muted-foreground">Total</span>
        <span className="flex items-center gap-1.5" style={{ color: matches ? '#10A882' : '#EF4444' }}>
          {matches && <CheckCircle size={12} weight="fill" />}
          Total: {fmtInr(Math.round(total))} of {fmtInr(netFee)}
        </span>
      </div>
    </div>
  );
}
